/**
 * AdminDeleteScreenConfirmPage - [UC-063]
 * Admin confirmation page for deleting a screen that is in use.
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '../components/ui';
import { api } from '../core/api-client';
import toast from 'react-hot-toast';

interface Screen {
  screenId: number;
  name: string;
  projectId: number;
  filePath: string;
}

export function AdminDeleteScreenConfirmPage() {
  const { screenId } = useParams<{ screenId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [screen, setScreen] = useState<Screen | null>(null);
  const [loading, setLoading] = useState(true);

  const projectId = searchParams.get('projectId');
  const screenName = searchParams.get('name');

  useEffect(() => {
    const loadScreen = async () => {
      if (!screenId) return;

      try {
        setLoading(true);
        const data = await api.get<Screen>(`/screens/${screenId}`);
        setScreen(data);
      } catch (error: any) {
        console.error('Failed to load screen:', error);
        toast.error(t('adminDeleteScreenConfirm.loadError'));
        // Navigate back to manage screens if can't load screen
        if (projectId) {
          navigate(`/admin/projects/${projectId}/screens`);
        }
      } finally {
        setLoading(false);
      }
    };

    loadScreen();
  }, [screenId, t, navigate, projectId]);

  const handleConfirmDelete = async () => {
    if (!screen) return;

    try {
      await api.delete(`/screens/${screen.screenId}`);
      toast.success(t('adminDeleteScreenConfirm.deleteSuccess'));
      navigate(`/admin/projects/${screen.projectId}/screens`);
    } catch (error: any) {
      console.error('Failed to delete screen:', error);
      toast.error(t('adminDeleteScreenConfirm.deleteError'));
    }
  };

  const handleCancel = () => {
    if (screen) {
      navigate(`/admin/projects/${screen.projectId}/screens`);
    } else if (projectId) {
      navigate(`/admin/projects/${projectId}/screens`);
    } else {
      navigate('/admin/dashboard');
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card>
          <div className="text-center py-8">
            <p className="text-gray-600">{t('adminDeleteScreenConfirm.loading')}</p>
          </div>
        </Card>
      </div>
    );
  }

  if (!screen && !screenName) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card>
          <div className="text-center py-8">
            <p className="text-gray-600">{t('adminDeleteScreenConfirm.notFound')}</p>
            <Button
              onClick={() => navigate('/admin/dashboard')}
              className="mt-4"
            >
              {t('adminDeleteScreenConfirm.backToDashboard')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const displayName = screen?.name || screenName || 'Unknown Screen';

  return (
    <div className="max-w-2xl mx-auto p-6">
      <Card>
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900">
            {t('adminDeleteScreenConfirm.title')}
          </h1>
        </div>

        <div className="mb-6">
          <p className="text-gray-700 mb-4">
            {t('adminDeleteScreenConfirm.message', { name: displayName })}
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-amber-800 mb-1">
                  {t('adminDeleteScreenConfirm.warningTitle')}
                </h3>
                <p className="text-sm text-amber-700">
                  {t('adminDeleteScreenConfirm.warningMessage')}
                </p>
              </div>
            </div>
          </div>

          {screen && (
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
              <div className="text-sm text-gray-600 space-y-2">
                <div className="flex justify-between py-2">
                  <span>{t('adminDeleteScreenConfirm.screenName')}:</span>
                  <span className="font-medium text-gray-900">{screen.name}</span>
                </div>
                <div className="flex justify-between py-2 border-t border-gray-200">
                  <span>{t('adminDeleteScreenConfirm.filePath')}:</span>
                  <span className="font-medium text-gray-900">{screen.filePath}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <Button
            onClick={handleCancel}
            variant="outline"
            className="w-full sm:w-auto"
          >
            {t('adminDeleteScreenConfirm.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white"
          >
            {t('adminDeleteScreenConfirm.confirmDelete')}
          </Button>
        </div>
      </Card>
    </div>
  );
}

export default AdminDeleteScreenConfirmPage;